import { sendParticipantHistoryReport } from '$app-web/api/endpoints';
import { PHREventTypes } from '$app-web/constants';
import { ObjectType } from '$app-web/types';
import { transformToSnakeCase } from '$app-web/utils';

function getRouteEventType(route: string) {
    switch (route) {
        case 'basics':
            return PHREventTypes.StudyBasicsPageOpened;
        case 'activities':
            return PHREventTypes.ActivitiesPageOpened;
        case 'data-sources':
            return PHREventTypes.DataSourcesPageOpened;
        case 'data-sources-beacons':
            return PHREventTypes.DataSourcesBeaconsPageOpened;
        case 'notification-templates':
            return PHREventTypes.NotificationTemplatesPageOpened;
        case 'notifications':
            return PHREventTypes.NotificationsPageOpened;
        case 'participation':
            return PHREventTypes.ParticipationPageOpened;
        case 'participation-pending-invitation':
            return PHREventTypes.ParticipationPendingInvitationPageOpened;
        case 'activity-sessions':
            return PHREventTypes.ActivitySessionsPageOpened;
        case 'audit-trail':
            return PHREventTypes.AuditTrailPageOpened;
        case 'survey-responses':
            return PHREventTypes.SurveyResponsesPageOpened;
        case 'data-export':
            return PHREventTypes.DataExportPageOpened;
        case 'researchers':
            return PHREventTypes.ResearchersPageOpened;
        default:
            return null;
    }
}

/*
    We could't use ReactQuery hook for Participant History Report feature because:
    ReactQuery should be used in the UI layer only (Component), while we need to
    call the related endpoints out of the React component.

    Note: We should update it to ReactQuery after refactoring the whole app
*/
async function sendReport(message: ObjectType & { eventType: number }, sessionUUID?: string) {
    try {
        await sendParticipantHistoryReport(transformToSnakeCase({ ...message, timeMs: Date.now() }), sessionUUID);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send participant history report:', error);
    }
}

export async function reportOpeningApplication(studiesId: Array<number>) {
    await sendReport({ eventType: PHREventTypes.ApplicationOpened, studyId: studiesId });
}

export async function reportUserSignUp() {
    await sendReport({ eventType: PHREventTypes.UserSignUp });
}

export async function reportUserSigningIn(studiesId: Array<number>) {
    await sendReport({ eventType: PHREventTypes.UserSignIn, studyId: studiesId });
}

export async function reportUserSigningOut(studiesId: Array<number>) {
    await sendReport({ eventType: PHREventTypes.UserLogout, studyId: studiesId });
}

export async function reportVisitingRoute(studyId: number, routeName = '-') {
    const relatedEvent = getRouteEventType(routeName);

    if (relatedEvent != null) {
        await sendReport({ eventType: relatedEvent, studyId: [studyId] });
    }
}

export async function reportEdenEvent(action: {
    eventType: number;
    ethicaId: string;
    studyId: number;
    consentType?: number;
    actionType?: string;
}) {
    const { eventType, ethicaId, studyId, actionType, consentType } = action;

    const message = {
        eventType,
        studyId: [studyId],
        ethicaId,
        ...(consentType == null ? {} : { consentType }),
        ...(actionType == null ? {} : { actionType }),
    };

    await sendReport(message);
}

export async function reportActivityOpened({
    studyId,
    activityId,
    sessionUuid,
}: {
    studyId: number;
    activityId: number;
    sessionUuid: string;
}) {
    await sendReport({ eventType: PHREventTypes.ActivityOpened, studyId: [studyId], activityId, sessionUuid });
}

export async function reportActivityClosed({
    studyId,
    activityId,
    sessionUuid,
}: {
    studyId: number;
    activityId: number;
    sessionUuid: string;
}) {
    await sendReport({ eventType: PHREventTypes.ActivityClosed, studyId: [studyId], activityId, sessionUuid });
}

export async function reportActivityFinished(payload: {
    sTimeMs: number;
    pTimeMs: number;
    rTimeMs: number;
    statusId: number;
    tlTypeId: number;
    activityId: number;
    studyId?: number;
    sessionUuid?: string;
}) {
    const { studyId, sessionUUID, ...rest } = { sessionUUID: undefined, ...payload };
    await sendReport({ eventType: PHREventTypes.ActivityFinished, ...rest, studyId: [studyId] }, sessionUUID);
}

export async function reportResponseUploadStarted({ studyId, sessionUUID }: { studyId: number; sessionUUID?: string }) {
    await sendReport({ eventType: PHREventTypes.ActivityResponsesUploadStarted, studyId: [studyId] }, sessionUUID);
}

export async function reportResponseUploadFinished({ studyId, sessionUUID }: { studyId: number; sessionUUID?: string }) {
    await sendReport({ eventType: PHREventTypes.ActivityResponsesUploadFinished, studyId: [studyId] }, sessionUUID);
}

export async function reportResponseUploadFailed({ studyId, sessionUUID }: { studyId: number; sessionUUID?: string }) {
    await sendReport({ eventType: PHREventTypes.ActivityResponsesUploadFailed, studyId: [studyId] }, sessionUUID);
}
